import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { getBackendClient } from "@/lib/backend";
import { isRecord } from "@/lib/utils/query-helpers";
import type { VoiceTurn } from "@/lib/voice/conversation";
import { holdConversationClose } from "./assistant-conversation-client";
import { type BusinessReceipt, storedReceipt } from "./assistant-business-receipts";
import type { DirectTextTurn } from "./hooks/use-direct-text";

// The operator's stored threads, read and managed through the `assistant-threads`
// edge function. Text turns are written by `assistant-text`; this module reads
// them back, stores live voice turns, and changes a thread's title, archive
// state, or existence.

export interface AssistantThreadSummary {
  threadId: string;
  title: string;
  createdAt: string;
  lastMessageAt: string;
  archivedAt: string | null;
}

export interface AssistantThreadMessage {
  id: string;
  seq: number;
  role: "operator" | "assistant";
  channel: "text" | "voice";
  body: string;
  clipped: boolean;
  conversationId: string | null;
  createdAt: string;
  /** The message's commands and proposals as they stand now, read by the server. */
  receipts: BusinessReceipt[];
}

export interface AssistantThreadPage {
  threadId: string;
  title: string;
  archivedAt: string | null;
  messages: AssistantThreadMessage[];
}

/** A refused or failed thread request; `status` 403 means the thread is gone or not this operator's. */
export class AssistantThreadError extends Error {
  constructor(
    readonly status: number | null,
    /** The backend's refusal text, when it gave one. */
    readonly refusal: string | null = null,
  ) {
    super("Thread request failed.");
  }
}

const backend = getBackendClient();

/** The backend adapter reports `statusCode`; a raw fetch failure may carry `status`. */
function statusOf(error: unknown): number | null {
  if (!isRecord(error)) return null;
  const status = error.statusCode ?? error.status;
  return typeof status === "number" ? status : null;
}

/** The backend's refusal text: the adapter's `message`, or a raw body's `error`. */
function refusalOf(error: unknown): string | null {
  if (!isRecord(error)) return null;
  const text = [error.error, error.message, error.code].find((value) => typeof value === "string");
  return typeof text === "string" ? text : null;
}

async function invokeThreads(body: Record<string, unknown>): Promise<Record<string, unknown>> {
  const write = body.action !== "list" && body.action !== "messages";
  const { data, error } = await backend.functions.invoke<unknown>("assistant-threads", {
    body,
    ...(write ? { retry: false as const } : {}),
  });
  if (error || !isRecord(data)) throw new AssistantThreadError(statusOf(error), refusalOf(error));
  return data;
}

function summaryFrom(row: unknown): AssistantThreadSummary[] {
  if (!isRecord(row) || typeof row.threadId !== "string") return [];
  return [
    {
      threadId: row.threadId,
      title: typeof row.title === "string" ? row.title : "",
      createdAt: String(row.createdAt),
      lastMessageAt: String(row.lastMessageAt),
      archivedAt: typeof row.archivedAt === "string" ? row.archivedAt : null,
    },
  ];
}

function messageFrom(row: unknown): AssistantThreadMessage[] {
  if (!isRecord(row) || typeof row.id !== "string" || typeof row.body !== "string") return [];
  if (row.role !== "operator" && row.role !== "assistant") return [];
  return [
    {
      id: row.id,
      seq: Number(row.seq),
      role: row.role,
      channel: row.channel === "voice" ? "voice" : "text",
      body: row.body,
      clipped: row.clipped === true,
      conversationId: typeof row.conversationId === "string" ? row.conversationId : null,
      createdAt: String(row.createdAt),
      receipts: Array.isArray(row.receipts)
        ? row.receipts.map(storedReceipt).filter((receipt) => receipt !== null)
        : [],
    },
  ];
}

export async function fetchAssistantThreads(archived: boolean): Promise<AssistantThreadSummary[]> {
  const data = await invokeThreads({ action: "list", archived });
  if (!Array.isArray(data.threads)) throw new AssistantThreadError(null);
  return data.threads.flatMap(summaryFrom);
}

/** The newest page of a thread's messages, oldest first. */
// The server's largest messages page, and how far back a resume reads.
const MESSAGES_PAGE = 500;
const MAX_RESUMED_MESSAGES = 2_000;

/**
 * A thread's stored messages, oldest first: newest page first, then older
 * pages while the server says more remain, up to MAX_RESUMED_MESSAGES.
 */
export async function fetchAssistantThreadPage(threadId: string): Promise<AssistantThreadPage> {
  let messages: AssistantThreadMessage[] = [];
  let beforeSeq: number | undefined;
  for (;;) {
    const data = await invokeThreads({
      action: "messages",
      threadId,
      limit: MESSAGES_PAGE,
      ...(beforeSeq === undefined ? {} : { beforeSeq }),
    });
    if (data.threadId !== threadId || !Array.isArray(data.messages))
      throw new AssistantThreadError(null);
    const page = data.messages.flatMap(messageFrom);
    messages = [...page, ...messages];
    const oldest = page[0]?.seq;
    if (data.truncated !== true || !oldest || messages.length >= MAX_RESUMED_MESSAGES)
      return {
        threadId,
        title: typeof data.title === "string" ? data.title : "",
        archivedAt: typeof data.archivedAt === "string" ? data.archivedAt : null,
        messages,
      };
    beforeSeq = oldest;
  }
}

// `assistant-text` aborts a turn at sixty seconds; an unanswered message older
// than that can no longer be answered.
const TEXT_TURN_WINDOW_MS = 60_000;

export const INCOMPLETE_STORED_TURN =
  "No answer was saved for this message. Check recent business commands and document receipts before retrying.";

/** The database keeps an operator message's head and an assistant reply's tail. */
function displayBody({ body, clipped, role }: AssistantThreadMessage): string {
  if (!clipped) return body;
  return role === "operator" ? `${body}…` : `…${body}`;
}

/**
 * Stored messages as transcript turns. A typed operator message its conversation
 * never answered is followed by an incomplete assistant turn, so a failed,
 * cancelled, or interrupted text turn reads as unanswered rather than as a
 * dangling question (a voice utterance may stand alone). The newest message is
 * left unmarked while its turn may still be running on another tab or device.
 * A clipped body shows where it was cut.
 */
export function turnsFromMessages(
  messages: AssistantThreadMessage[],
  now = Date.now(),
): DirectTextTurn[] {
  return messages.flatMap((message, index) => {
    const turn: DirectTextTurn = {
      id: message.id,
      channel: message.channel,
      isFinal: true,
      role: message.role,
      text: displayBody(message),
      ...(message.conversationId ? { conversationId: message.conversationId } : {}),
      ...(message.receipts.length ? { receipts: message.receipts } : {}),
    };
    if (message.role !== "operator" || message.channel !== "text") return [turn];
    // The next message of the same conversation answers it, however another
    // tab's or device's conversation interleaves with it.
    const reply = messages.find(
      (later, at) => at > index && later.conversationId === message.conversationId,
    );
    const answered = reply?.role === "assistant";
    const inFlight = !reply && now - Date.parse(message.createdAt) < TEXT_TURN_WINDOW_MS;
    if (answered || inFlight) return [turn];
    return [
      turn,
      {
        id: `${message.id}-unanswered`,
        // A settled record: closing the conversation must not rewrite it.
        isFinal: true,
        isIncomplete: true,
        role: "assistant",
        text: INCOMPLETE_STORED_TURN,
      },
    ];
  });
}

// Pauses before retrying an append the backend did not answer; past them the
// turn is skipped so later turns are not held behind it.
const APPEND_RETRY_MS = [1_000, 3_000];

/**
 * Stores one live voice conversation's finalized turns in its thread, in the
 * order they finalized. A turn's id (`role-index`) is its client message key,
 * so a corrected reply replaces the stored one. A refusal (the conversation
 * ended or is not this operator's live voice one) stops storing for this
 * conversation only; an outage is retried briefly and never ends the call.
 */
export function recordVoiceTurns(
  conversationId: string,
  onStored: () => void,
  onRefused: () => void = () => undefined,
) {
  let refused = false;
  let queue = Promise.resolve();
  const store = async ({ id, role, text }: VoiceTurn) => {
    for (const pause of [...APPEND_RETRY_MS, null]) {
      if (refused) return;
      try {
        await invokeThreads({ action: "append", conversationId, role, text, clientMessageId: id });
        onStored();
        return;
      } catch (error) {
        const status = error instanceof AssistantThreadError ? error.status : null;
        // Only the conversation's own refusal (expired, revoked, not owned) ends
        // storing for the call; another 403, such as a failed staff check, skips
        // this turn and the next one tries again.
        const conversationRefused =
          status === 403 &&
          error instanceof AssistantThreadError &&
          error.refusal === "Conversation unavailable";
        if (conversationRefused && !refused) {
          refused = true;
          onRefused();
        }
        if (pause === null || (status !== null && status < 500)) return;
        await new Promise((resolve) => setTimeout(resolve, pause));
      }
    }
  };
  return (turn: VoiceTurn) => {
    queue = queue.then(() => store(turn));
    holdConversationClose(conversationId, queue);
  };
}

export const assistantThreadKeys = {
  all: (userId: string | null) => ["assistant-threads", userId] as const,
  list: (userId: string | null, archived: boolean) =>
    ["assistant-threads", userId, "list", archived] as const,
  messages: (userId: string | null, threadId: string) =>
    ["assistant-threads", userId, "messages", threadId] as const,
};

/** One read path for a thread's stored messages, shared by resume and restore. */
export function loadAssistantThreadPage(
  queryClient: QueryClient,
  userId: string | null,
  threadId: string,
): Promise<AssistantThreadPage> {
  return queryClient.fetchQuery({
    queryKey: assistantThreadKeys.messages(userId, threadId),
    queryFn: () => fetchAssistantThreadPage(threadId),
    // Another tab, device, or a voice call may have stored turns since; a
    // resume always reads them rather than the app's five-minute cache.
    staleTime: 0,
  });
}

export function useAssistantThreads(userId: string | null, archived = false) {
  return useQuery({
    queryKey: assistantThreadKeys.list(userId, archived),
    queryFn: () => fetchAssistantThreads(archived),
    enabled: Boolean(userId),
  });
}

type ThreadChange =
  | { action: "rename"; threadId: string; title: string }
  | { action: "archive" | "unarchive" | "delete"; threadId: string };

/** Rename, archive, unarchive, and delete; each refreshes every view of the operator's threads. */
export function useAssistantThreadActions(userId: string | null) {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (change: ThreadChange) => invokeThreads(change),
    onSettled: () => queryClient.invalidateQueries({ queryKey: assistantThreadKeys.all(userId) }),
  });
  const run = (change: ThreadChange) => mutation.mutateAsync(change).then(() => undefined);
  return {
    pending: mutation.isPending,
    rename: (threadId: string, title: string) => run({ action: "rename", threadId, title }),
    archive: (threadId: string) => run({ action: "archive", threadId }),
    unarchive: (threadId: string) => run({ action: "unarchive", threadId }),
    remove: (threadId: string) => run({ action: "delete", threadId }),
  };
}

const CURRENT_THREAD_KEY = "xan.assistant.current_thread";

function currentThreadKey(userId: string): string {
  return `${CURRENT_THREAD_KEY}.${userId}`;
}

/** The thread this operator last had open on this browser, if any. */
export function readCurrentThread(userId: string | null): string | null {
  if (!userId) return null;
  try {
    return localStorage.getItem(currentThreadKey(userId)) || null;
  } catch {
    return null;
  }
}

export function writeCurrentThread(userId: string | null, threadId: string | null): void {
  if (!userId) return;
  try {
    if (threadId) localStorage.setItem(currentThreadKey(userId), threadId);
    else localStorage.removeItem(currentThreadKey(userId));
  } catch {
    // Storage unavailable: the thread still resumes for this tab.
  }
}
